import { Build, forceUpdate, h, Host } from "@stencil/core";
import { registryProperty } from "../../../common/registry-properties";
import { getImagePathCallbackAccordion } from "./accordion/models";
import { getActionListPathCallback } from "./action-list/models";
import { getComboBoxImagePathCallback } from "./combo-box/models";
import { getImagePathCallbackEdit } from "./edit/models";
import { getImagePathCallbackImage } from "./image/models";
import { getImagePathCallbackTab } from "./tab/models";
import { getImagePathCallbackTreeView } from "./tree-view/models";
import { defineControlMarkupWithoutUIModelReact, defineControlMarkupWithoutUIModelStencil, defineControlMarkupWithUIModelReact, defineControlMarkupWithUIModelStencil } from "./utils";
import { disableAccessibilityReports, enableAccessibilityReports } from "../../../common/analysis/reports";
import { getDesignSystem, storeDesignSystem } from "../../models/ds-manager.js";
import { getLanguageDirection, setLanguageDirectionInBrowser, storeLanguageDirection } from "../../models/language-manager.js";
import { getTheme, setThemeInBrowser, storeTheme } from "../../models/theme-manager.js";
import { getDropdownImagePathCallback } from "./action-menu/models";
import { findComponentMetadataUsingURLHash } from "./pages";
import { ASIDE_WIDGET, colorSchemeModel, CONFIGURATION_WIDGET, designSystemModel, flexibleLayoutConfiguration, flexibleLayoutPlaygroundConfiguration, HEADER_WIDGET, languageDirectionModel, MAIN_SECTION, MAIN_WIDGET, USAGE_REACT, USAGE_STENCIL_JS } from "./renders";
disableAccessibilityReports();
// Only enable reports in dev mode
if (Build.isDev) {
    enableAccessibilityReports();
}
const SHOWCASE_STYLES = [
    {
        name: "showcase-styles",
        url: "showcase/showcase-flexible-layout.css"
    }
];
const UNANIMO_THEME = [
    {
        name: "unanimo",
        url: "https://unpkg.com/@genexus/unanimo@latest/dist/bundles/css/all.css"
    },
    {
        name: "unanimo-extra",
        url: "showcase/unanimo-extra-styles.css"
    }
];
const UNANIMO_BASE_THEME = [
    {
        name: "unanimo/base",
        url: "https://unpkg.com/@genexus/unanimo@latest/dist/bundles/css/base/base.css"
    }
];
const MERCURY_THEME = [
    {
        name: "mercury",
        url: "https://unpkg.com/@genexus/mercury@latest/dist/bundles/css/all.css"
    },
    {
        name: "mercury-extra",
        url: "showcase/mercury-extra-styles.css"
    }
];
const MERCURY_BASE_THEME = [
    {
        name: "mercury/base",
        url: "https://unpkg.com/@genexus/mercury@latest/dist/bundles/css/base/base.css"
    }
];
registryProperty("getImagePathCallback", {
    "ch-accordion-render": getImagePathCallbackAccordion,
    "ch-action-list-render": getActionListPathCallback,
    "ch-action-menu-render": getDropdownImagePathCallback,
    "ch-checkbox": getImagePathCallbackEdit,
    "ch-combo-box-render": getComboBoxImagePathCallback,
    "ch-edit": getImagePathCallbackEdit,
    "ch-image": getImagePathCallbackImage,
    "ch-tab-render": getImagePathCallbackTab,
    "ch-tree-view-render": getImagePathCallbackTreeView
});
// registryControlProperty("getImagePathCallback", "ch-image", getImagePathCallbackImage)
const defaultRenderForEachPropertyType = {
    boolean: "checkbox",
    enum: "combo-box",
    number: "input-number",
    string: "input",
    object: "independent-properties",
    style: "independent-properties"
};
export class ChShowcase {
    constructor() {
        this.componentMetadata = undefined;
        this.colorScheme = undefined;
        this.designSystem = undefined;
        this.languageDirection = undefined;
        this.packageVersion = undefined;
        this.pages = undefined;
        this.stories = undefined;
        this.status = undefined;
    }
    #storyFirstRender = true;
    #showcaseStory;
    #showcaseCustomStory;
    #showcaseStoryCheckboxes;
    #showcaseStoryComboBoxes;
    #showcaseStoryInput;
    #showcaseStoryInputNumber;
    #showcaseStoryRadioGroups;
    #handlerInitializationMapping = {
        boolean: (property, parentObject) => {
            const showcaseStoryState = this.#showcaseStory.state;
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            // Initialize state
            showcaseStoryState[propertyGroupId] = property.value;
            if (property.render === "switch") {
                // TODO
            }
            // Checkbox by default
            else {
                this.#showcaseStoryCheckboxes ??= new Map();
                this.#showcaseStoryCheckboxes.set(propertyGroupId, () => {
                    const checkboxCurrentValue = showcaseStoryState[propertyGroupId];
                    showcaseStoryState[propertyGroupId] = !checkboxCurrentValue;
                    // Verify if the checkbox is inside of an object
                    if (parentObject) {
                        const oldGroupInfo = showcaseStoryState[parentObject.id];
                        showcaseStoryState[parentObject.id] = {
                            ...oldGroupInfo,
                            [property.id]: !checkboxCurrentValue
                        };
                    }
                    forceUpdate(this);
                });
            }
        },
        enum: (property, parentObject) => {
            const showcaseStoryState = this.#showcaseStory.state;
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            // Initialize state
            showcaseStoryState[propertyGroupId] = property.value;
            const eventHandler = (event) => {
                showcaseStoryState[propertyGroupId] = event.detail;
                // Verify if the combo-box/radio-group is inside of an object
                if (parentObject) {
                    const oldGroupInfo = showcaseStoryState[parentObject.id];
                    showcaseStoryState[parentObject.id] = {
                        ...oldGroupInfo,
                        [property.id]: event.detail
                    };
                }
                forceUpdate(this);
            };
            // Radio Group
            if (property.render === "radio-group") {
                this.#showcaseStoryRadioGroups ??= new Map();
                this.#showcaseStoryRadioGroups.set(propertyGroupId, {
                    handler: eventHandler,
                    model: property.values
                });
            }
            // Combo Box by default
            else {
                this.#showcaseStoryComboBoxes ??= new Map();
                this.#showcaseStoryComboBoxes.set(propertyGroupId, {
                    handler: eventHandler,
                    model: property.values
                });
            }
        },
        number: (property, parentObject) => {
            const showcaseStoryState = this.#showcaseStory.state;
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            // Initialize state
            showcaseStoryState[propertyGroupId] = property.value;
            this.#showcaseStoryInputNumber ??= new Map();
            this.#showcaseStoryInputNumber.set(propertyGroupId, (event) => {
                const inputCurrentValue = event.target.value;
                showcaseStoryState[propertyGroupId] = inputCurrentValue;
                // Verify if the input-number is inside of an object
                if (parentObject) {
                    const oldGroupInfo = showcaseStoryState[parentObject.id];
                    showcaseStoryState[parentObject.id] = {
                        ...oldGroupInfo,
                        [property.id]: inputCurrentValue
                    };
                }
                forceUpdate(this);
            });
        },
        object: (property) => {
            const showcaseStoryState = this.#showcaseStory.state;
            // Initialize the state as an empty object
            showcaseStoryState[property.id] = {};
        },
        style: (property) => {
            const showcaseStoryState = this.#showcaseStory.state;
            // Initialize the state as an empty object
            showcaseStoryState[property.id] = {};
        },
        string: (property, parentObject) => {
            const showcaseStoryState = this.#showcaseStory.state;
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            // Initialize state
            showcaseStoryState[propertyGroupId] = property.value;
            this.#showcaseStoryInput ??= new Map();
            this.#showcaseStoryInput.set(propertyGroupId, (event) => {
                const inputCurrentValue = event.target.value;
                showcaseStoryState[propertyGroupId] = inputCurrentValue;
                // Verify if the input-number is inside of an object
                if (parentObject) {
                    const oldGroupInfo = showcaseStoryState[parentObject.id];
                    showcaseStoryState[parentObject.id] = {
                        ...oldGroupInfo,
                        [property.id]: inputCurrentValue
                    };
                }
                forceUpdate(this);
            });
        }
    };
    #getPropertyId = (property, parentObject) => (parentObject ? `${parentObject.id}_${property.id}` : property.id);
    #flexibleLayoutRender = {
        [HEADER_WIDGET]: () => (h("header", { key: HEADER_WIDGET, slot: HEADER_WIDGET, class: "header" }, h("ch-theme", { attachStyleSheets: this.designSystem === "unanimo", model: UNANIMO_THEME }), h("ch-theme", { attachStyleSheets: this.designSystem === "mercury", model: MERCURY_THEME }), h("div", { class: "header-start" }, h("a", { id: "chameleon-home", class: "home heading-2", href: "#", "aria-label": "Home" }, "Chameleon"), h("span", { id: "chameleon-version", class: "text-body-2" }, this.packageVersion)), h("div", { class: "header-end" }, h("ch-segmented-control-render", { model: designSystemModel, selectedId: this.designSystem, onSelectedItemChange: this.#handleDesignSystemChange }), h("ch-segmented-control-render", { model: colorSchemeModel, selectedId: this.colorScheme, onSelectedItemChange: this.#handleColorSchemeChange }), h("ch-segmented-control-render", { model: languageDirectionModel, selectedId: this.languageDirection, onSelectedItemChange: this.#handleLanguageDirectionChange }), h("a", { href: "https://github.com/genexuslabs/chameleon-controls-library", "aria-label": "Chameleon's GitHub", class: "icon-background github-logo", target: "_blank", title: "Chameleon's GitHub" })))),
        [ASIDE_WIDGET]: () => (h("aside", { key: ASIDE_WIDGET, slot: ASIDE_WIDGET, class: "ch-showcase__aside" }, h("ch-edit", { accessibleName: "Search", class: "input input-search sidebar__search-input", placeholder: "Search", type: "search" }), h("ch-navigation-list-render", { class: "navigation-list navigation-list-primary", model: this.pages, expandSelectedLink: true, selectedLink: this.componentMetadata?.link
                ? this.componentMetadata
                : undefined, selectedLinkIndicator: true, expandableButtonPosition: "end" }))),
        [MAIN_SECTION]: () => (h("main", { key: MAIN_SECTION, slot: MAIN_SECTION }, this.#renderMainSection()))
    };
    #flexibleLayoutPlaygroundRenders = {
        [MAIN_WIDGET]: () => (h("div", { key: MAIN_WIDGET, slot: MAIN_WIDGET, class: "card" }, h("ch-theme", { attachStyleSheets: this.designSystem === "unanimo", model: UNANIMO_THEME }), h("ch-theme", { attachStyleSheets: this.designSystem === "mercury", model: MERCURY_THEME }), this.#componentId === "barcode-scanner" ? (h("slot", { name: "ch-barcode-scanner" })) : (this.#showcaseStory.render(this.designSystem)))),
        [USAGE_STENCIL_JS]: () => (h("div", { key: USAGE_STENCIL_JS, slot: USAGE_STENCIL_JS, class: "card card-markup" }, h("button", { class: "button-tertiary button-icon-only copy-button icon-mask", title: "Copy markup", type: "button", onClick: this.#handleCopyMarkup, ref: el => (this.#copyButtonRef = el) }), h("ch-code", { key: USAGE_STENCIL_JS, slot: USAGE_STENCIL_JS, class: "code", language: "typescript", value: this.#showcaseStory.markupWithUIModel
                ? defineControlMarkupWithUIModelStencil(this.#showcaseStory.markupWithUIModel.uiModel(), this.#showcaseStory.markupWithUIModel.uiModelType, this.#showcaseStory.markupWithUIModel.render.stencil(this.designSystem))
                : defineControlMarkupWithoutUIModelStencil(this.#showcaseStory.markupWithoutUIModel
                    ? this.#showcaseStory.markupWithoutUIModel.stencil(this.designSystem)
                    : "To be defined") }))),
        [USAGE_REACT]: () => (h("div", { key: USAGE_REACT, slot: USAGE_REACT, class: "card card-markup" }, h("button", { class: "button-tertiary button-icon-only copy-button icon-mask", title: "Copy markup", type: "button", onClick: this.#handleCopyMarkup, ref: el => (this.#copyButtonRef = el) }), h("ch-code", { key: USAGE_REACT, slot: USAGE_REACT, class: "code", language: "typescript", value: this.#showcaseStory.markupWithUIModel
                ? defineControlMarkupWithUIModelReact(this.#showcaseStory.markupWithUIModel.uiModel(), this.#showcaseStory.markupWithUIModel.uiModelType, this.#showcaseStory.markupWithUIModel.render.react(this.designSystem))
                : defineControlMarkupWithoutUIModelReact(this.#showcaseStory.markupWithoutUIModel
                    ? this.#showcaseStory.markupWithoutUIModel.react(this.designSystem)
                    : "To be defined") }))),
        [CONFIGURATION_WIDGET]: () => (h("div", { key: CONFIGURATION_WIDGET, slot: CONFIGURATION_WIDGET, class: "card card-properties" }, this.#showcaseStory.properties.map(this.#propertyGroupRender)))
    };
    #componentId;
    // Refs
    #copyButtonRef;
    #flexibleLayoutRef;
    #playgroundRef;
    #iframeRef;
    el;
    componentMetadataChange(newComponentMetadata) {
        const showcaseStory = this.#showcaseStory || this.#showcaseCustomStory;
        this.#storyFirstRender = true;
        // Story disconnectedCallback
        if (showcaseStory && showcaseStory.disconnectedCallback) {
            showcaseStory.disconnectedCallback();
        }
        this.#componentId = newComponentMetadata?.link.url.replace("#", "");
        this.#checkShowcaseStoryMapping(this.#componentId);
    }
    colorSchemeChange(newColorSchemeValue) {
        // The showcase does not render a iframe
        if (this.#showcaseStory) {
            return;
        }
        this.#iframeRef?.contentWindow.postMessage(newColorSchemeValue, `${window.location.origin}/showcase/pages/${this.componentMetadata.link.url.replace("#", "")}.html`);
    }
    designSystemChange(newDSValue) {
        // The showcase does not render a iframe
        if (this.#showcaseStory) {
            return;
        }
        this.#iframeRef?.contentWindow.postMessage(newDSValue, `${window.location.origin}/showcase/pages/${this.componentMetadata.link.url.replace("#", "")}.html`);
    }
    onHashChange() {
        if (!window.location.hash) {
            this.componentMetadata = undefined;
            return;
        }
        const newComponentMetadata = findComponentMetadataUsingURLHash(this.pages, window.location.hash);
        if (newComponentMetadata) {
            this.componentMetadata = newComponentMetadata;
        }
    }
    #checkShowcaseStoryMapping = (componentId) => {
        // Free the memory
        this.#showcaseStoryCheckboxes = undefined;
        this.#showcaseStoryComboBoxes = undefined;
        this.#showcaseStoryInput = undefined;
        this.#showcaseStoryInputNumber = undefined;
        this.#showcaseStoryRadioGroups = undefined;
        this.#showcaseStory = this.stories.playground[componentId] ?? undefined;
        this.#showcaseCustomStory = this.stories.custom[componentId] ?? undefined;
        if (this.#showcaseStory) {
            const properties = this.#showcaseStory.properties;
            // Initialize state with default values
            properties.forEach(group => {
                group.properties.forEach(property => this.#initializePropertyInState(property));
            });
        }
    };
    #initializePropertyInState = (property, object) => {
        if (property.type === "object") {
            // Initialize the object
            this.#handlerInitializationMapping[property.type](property);
            // Initialize all object properties. "property" in this case is the "object"
            property.properties.forEach(childProperty => this.#initializePropertyInState(childProperty, property));
        }
        else if (property.type === "style") {
            // Initialize the object
            this.#handlerInitializationMapping[property.type](property);
            // Initialize all object properties. "property" in this case is the "style"
            property.properties.forEach(childProperty => this.#initializePropertyInState(childProperty, property));
        }
        else {
            // TODO: Improve type inference
            this.#handlerInitializationMapping[property.type](property, object);
        }
    };
    #propertyGroupRender = (group, index) => [
        index !== 0 && h("hr", null),
        h("fieldset", { style: group.columns
                ? { "grid-template-columns": `repeat(${group.columns}, 1fr)` }
                : null }, h("legend", { class: "heading-4" }, group.caption), this.#renderProperties(group.properties))
    ];
    #renderProperties = (properties, object) => properties.map(property => this.#propertyRender[property.render ?? defaultRenderForEachPropertyType[property.type]](property, object));
    #propertyRenderWithLabel = (property, content) => (h("div", { class: "form-field", style: property.columnSpan
            ? { "grid-column": `1 / ${property.columnSpan + 1}` }
            : null }, content));
    #propertyRender = {
        checkbox: (property, parentObject) => (h("ch-checkbox", { accessibleName: property.accessibleName, caption: property.caption, class: "checkbox", style: property.columnSpan
                ? { "grid-column": `1 / ${property.columnSpan + 1}` }
                : null, checkedValue: "true", unCheckedValue: "false", value: property.value.toString(), onInput: this.#showcaseStoryCheckboxes.get(this.#getPropertyId(property, parentObject)) })),
        "combo-box": (property, parentObject) => {
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            return this.#propertyRenderWithLabel(property, [
                property.caption && (h("label", { class: "label", htmlFor: propertyGroupId }, property.caption)),
                h("ch-combo-box-render", { id: propertyGroupId, accessibleName: property.accessibleName, class: "combo-box", model: this.#showcaseStoryComboBoxes.get(propertyGroupId).model, value: property.value.toString(), onInput: this.#showcaseStoryComboBoxes.get(propertyGroupId).handler })
            ]);
        },
        input: (property, parentObject) => {
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            return this.#propertyRenderWithLabel(property, [
                property.caption && (h("label", { class: "label", htmlFor: propertyGroupId }, property.caption)),
                h("input", { id: propertyGroupId, "aria-label": property.accessibleName ?? null, class: "input", type: "text", value: property.value?.toString(), onInput: this.#showcaseStoryInput.get(propertyGroupId) })
            ]);
        },
        "input-number": (property, parentObject) => {
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            return this.#propertyRenderWithLabel(property, [
                property.caption && (h("label", { class: "label", htmlFor: propertyGroupId }, property.caption)),
                h("input", { id: propertyGroupId, "aria-label": property.accessibleName ?? null, class: "input", type: "number", value: property.value?.toString(), onInput: this.#showcaseStoryInputNumber.get(propertyGroupId) })
            ]);
        },
        "independent-properties": (property) => this.#renderProperties(property.properties, property),
        "radio-group": (property, parentObject) => {
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            return this.#propertyRenderWithLabel(property, [
                property.caption && (h("label", { class: "label", htmlFor: propertyGroupId }, property.caption)),
                h("ch-radio-group-render", { id: propertyGroupId, "aria-label": property.accessibleName ?? null, class: "radio-group", model: this.#showcaseStoryRadioGroups.get(propertyGroupId).model, value: property.value.toString(), onChange: this.#showcaseStoryRadioGroups.get(propertyGroupId).handler })
            ]);
        },
        textarea: (property, parentObject) => {
            const propertyGroupId = this.#getPropertyId(property, parentObject);
            return this.#propertyRenderWithLabel(property, [
                property.caption && (h("label", { class: "label", htmlFor: propertyGroupId }, property.caption)),
                h("textarea", { id: propertyGroupId, "aria-label": property.accessibleName ?? null, class: "input", value: property.value.toString(), onInput: this.#showcaseStoryInput.get(propertyGroupId) })
            ]);
        }
    };
    #handleCopyMarkup = (event) => {
        event.stopPropagation();
        navigator.clipboard.writeText(this.#copyButtonRef.nextElementSibling.value);
    };
    #handleDesignSystemChange = () => {
        const newDS = getDesignSystem() === "unanimo" ? "mercury" : "unanimo";
        storeDesignSystem(newDS);
        this.designSystem = newDS;
    };
    #handleColorSchemeChange = () => {
        const colorScheme = getTheme() === "light" ? "dark" : "light";
        storeTheme(colorScheme);
        setThemeInBrowser(colorScheme);
        this.colorScheme = colorScheme;
    };
    #handleLanguageDirectionChange = () => {
        const newLanguageDirection = getLanguageDirection() === "rtl" ? "ltr" : "rtl";
        storeLanguageDirection(newLanguageDirection);
        setLanguageDirectionInBrowser(newLanguageDirection);
        this.languageDirection = newLanguageDirection;
    };
    // #iframeRender = () => (
    //   <iframe
    //     src={"/showcase/pages/" + this.componentMetadata.id + ".html"}
    //     frameborder="0"
    //     ref={el => (this.#iframeRef = el)}
    //   ></iframe>
    // );
    #renderMainSection = () => {
        if (this.#showcaseStory) {
            return (h("div", { class: "ch-showcase__playground" }, h("h1", { class: "heading-1" }, this.componentMetadata.caption, " (", this.componentMetadata.metadata, ")"), h("ch-flexible-layout-render", { class: "flexible-layout ch-showcase__flexible-layout-playground", model: flexibleLayoutPlaygroundConfiguration, renders: this.#flexibleLayoutPlaygroundRenders, theme: SHOWCASE_STYLES, ref: el => (this.#playgroundRef = el) }, h("slot", { name: "ch-barcode-scanner", slot: "ch-barcode-scanner" }))));
        }
        if (this.#showcaseCustomStory) {
            return (h("div", { class: "ch-showcase__playground" }, h("h1", { class: "heading-1" }, this.componentMetadata.caption, " (", this.componentMetadata.metadata, ")"), this.#showcaseCustomStory.render(this.designSystem)));
        }
        return this.stories?.landing.render(this.designSystem);
    };
    async connectedCallback() {
        this.colorScheme ||= getTheme();
        this.designSystem ||= getDesignSystem();
        this.languageDirection ||= getLanguageDirection();
        if (!this.pages || !this.stories) {
            const [pagesBundle, storiesBundle, landingBundle] = await Promise.all([
                import("./pages"),
                import("./showcase-stories"),
                import("./landing")
            ]);
            this.pages = pagesBundle.showcasePages;
            this.stories = {
                // TODO: Improve type safety
                playground: storiesBundle.showcaseStories,
                custom: storiesBundle.showcaseCustomStories,
                landing: landingBundle.landingStory
            };
        }
        // Set initial page using the URL
        this.onHashChange();
        if (this.componentMetadata) {
            this.#componentId = this.componentMetadata?.link.url.replace("#", "");
            this.#checkShowcaseStoryMapping(this.#componentId);
        }
    }
    componentDidRender() {
        if (this.#flexibleLayoutRef) {
            forceUpdate(this.#flexibleLayoutRef);
        }
        // Wait until all references have been updated for the first-level flexible-layout component
        requestAnimationFrame(() => {
            const showcaseStory = this.#showcaseStory || this.#showcaseCustomStory;
            // This is a WA to make reactive the URL changes in the page
            // To remove this WA we must implement slots in the flexible-layout-render,
            // but to implement slots we should also implement Shadow DOM
            if (this.#playgroundRef) {
                forceUpdate(this.#playgroundRef);
            }
            if (this.#storyFirstRender) {
                this.#storyFirstRender = false;
                requestAnimationFrame(() => {
                    // Story did load
                    if (showcaseStory?.storyDidLoad) {
                        showcaseStory.storyDidLoad();
                    }
                    // Story did render
                    if (showcaseStory?.storyDidRender) {
                        showcaseStory.storyDidRender();
                    }
                });
            }
            // Story did render
            else if (showcaseStory?.storyDidRender) {
                showcaseStory.storyDidRender();
            }
        });
    }
    render() {
        return (h(Host, { key: '7354e7325f856bb6dea8afd659cf0f283c943bd7' }, h("ch-flexible-layout-render", { key: '52f9ef9fd4701f6d2235aa80659eb30ab2ac0df7', class: "flexible-layout",
            // TODO: Fix error when adding the closeButton and closing the last item
            model: flexibleLayoutConfiguration, renders: this.#flexibleLayoutRender, theme: SHOWCASE_STYLES, ref: el => (this.#flexibleLayoutRef = el) }, this.#componentId === "barcode-scanner" &&
            this.#showcaseStory.render(this.designSystem)), h("ch-theme", { key: 'a25c6f05104cd903f0149f0c6192158c6df3a96e', attachStyleSheets: this.designSystem === "unanimo", model: UNANIMO_BASE_THEME }), h("ch-theme", { key: 'e3b36dd37110e2dc5bd95a9f3431281b13d31e31', attachStyleSheets: this.designSystem === "mercury", model: MERCURY_BASE_THEME })));
    }
    static get is() { return "ch-showcase"; }
    static get originalStyleUrls() {
        return {
            "$": ["showcase.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["showcase.css"]
        };
    }
    static get properties() {
        return {
            "colorScheme": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "\"light\" | \"dark\"",
                    "resolved": "\"dark\" | \"light\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the theme used in the iframe of the control"
                },
                "attribute": "color-scheme",
                "reflect": false
            },
            "designSystem": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "\"mercury\" | \"unanimo\"",
                    "resolved": "\"mercury\" | \"unanimo\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the design system used in the iframe of the control"
                },
                "attribute": "design-system",
                "reflect": false
            },
            "languageDirection": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "\"ltr\" | \"rtl\"",
                    "resolved": "\"ltr\" | \"rtl\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the language direction of the document"
                },
                "attribute": "language-direction",
                "reflect": false
            },
            "packageVersion": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the version of the showcase displayed in the header"
                },
                "attribute": "package-version",
                "reflect": false
            },
            "pages": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "NavigationListModel | undefined",
                    "resolved": "NavigationListItemModel[]",
                    "references": {
                        "NavigationListModel": {
                            "location": "import",
                            "path": "../../../components",
                            "id": "src/components.d.ts::NavigationListModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the pages that will be displayed in the sidebar"
                }
            },
            "stories": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "| {\n        custom: { [key: string]: ShowcaseCustomStory };\n        landing: ShowcaseCustomStory;\n        playground: ShowcaseStories;\n      }\n    | undefined",
                    "resolved": "{ custom: { [key: string]: ShowcaseCustomStory; }; landing: ShowcaseCustomStory; playground: ShowcaseStories; }",
                    "references": {
                        "ShowcaseCustomStory": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/showcase/assets/components/types.ts::ShowcaseCustomStory"
                        },
                        "ShowcaseStories": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/showcase/assets/components/types.ts::ShowcaseStories"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the stories for the showcase."
                }
            },
            "status": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"developer-preview\" | \"experimental\" | \"stable\"",
                    "resolved": "\"developer-preview\" | \"experimental\" | \"stable\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the development status of the control.\n  - \"experimental\": The control is in its early stages of the development.\n    This phase is often useful for testing the control early, but it is\n    very likely that the interface will change from the final version.\n\n    Breaking changes for the control can be applied in \"patch\" tags.\n\n  - \"developer-preview\": The control is in its final stages of the\n    development. The interface and behaviors to implement the control are\n    almost complete. The interface of the control should not change so much\n    from the final version.\n\n    Breaking changes for the control can be applied in \"major\" tags.\n\n  - \"stable\": The control's development is stable and can be safety used\n    in production environments.\n\n    Breaking changes for the control can be applied in \"major\" tags. In\n    some cases, two \"major\" tags would be used to deprecate a behavior in\n    the control."
                },
                "attribute": "status",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "componentMetadata": {}
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "componentMetadata",
                "methodName": "componentMetadataChange"
            }, {
                "propName": "colorScheme",
                "methodName": "colorSchemeChange"
            }, {
                "propName": "designSystem",
                "methodName": "designSystemChange"
            }];
    }
    static get listeners() {
        return [{
                "name": "hashchange",
                "method": "onHashChange",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
